<script>
  import { createEventDispatcher, getContext } from "svelte";
  import { Link } from "svelte-routing";
  import { tooltip } from "svelte-utilities";

  import CircleXIcon from "@local/assets/icons/circle-x.svg";
  import Viewport from "#lib/drawing/ViewportFixed.svelte";
  import ViewportLg from "#lib/drawing/Viewport.svelte";
  import SidebarSection from "./SidebarSection.svelte";
  import TextInput from "./TextInput.svelte";
  import SelectInput from "./SelectInput.svelte";
  import BooleanInput from "./BooleanInput.svelte";
  import FlagInput from "./FlagInput.svelte";
  import LibraryInput from "./LibraryInput.svelte";
  import DimtextInput from "./DimtextInput.svelte";
  import ContenteditableInput from "./ContenteditableInput.svelte";
  import getTotalThickness from "@local/extensions/formatters/get-total-thickness";
  import MakeupThumbnail from "#lib/MakeupThumbnail.svelte";

  import typeDrawing from "@local/extensions/drawing/type-drawing.js";
  import typeDrawingDetailed from "@local/extensions/drawing/type-drawing-detailed.js";
  import { genericLibrary } from "#src/extensions/makeup-library.js";
  import { expandRightPanel } from "#src/stores/ui.js";
  import { drawingSrc as getDrawingSrc } from "#src/api";

  export let disabled = false;
  export let type;
  export let settings;
  export let multiple = false;

  const dispatch = createEventDispatcher();
  const makeupLibrary = getContext("makeupLibrary");
  const supplier = getContext("supplier");
  const suppliers = getContext("suppliers");

  let refreshKey = crypto.randomUUID();

  $: totalThickness = getTotalThickness(type, settings);
  $: makeups = $makeupLibrary.map((m) => m.records).flat();
  $: drawingSrc = !multiple && type.drawing && getDrawingSrc(type.drawing);

  function updateType(id, prop, value) {
    dispatch("updateType", { id, prop, value });
  }

  function product(type) {
    if (!type.product_id) {
      return null;
    }

    return { id: type.product_id, name: type.name };
  }

  function chooseProduct(e) {
    const makeup = e.detail.value;
    dispatch("updateType", {
      id: type.id,
      diff: {
        name: makeup.name,
        product_id: makeup.id,
        data: {
          product_id: makeup.id,
          ...makeup.data,
        },
      },
    });
  }

  function removeDrawing() {
    dispatch("updateType", { id: type.id, prop: "drawing", value: null });
  }

  function updateSupplier(e) {
    dispatch("updateSupplier", e.detail);
  }
</script>

{#if !multiple}
  <div class="thumbnail-image">
    {#if $expandRightPanel}
      <SidebarSection>
        <div class="relative">
          <Link to={`types/${type.id}`}>
            <div class="flex items-center justify-center">
              {#key refreshKey}
                {#if type.drawing && type.replace_with_drawing}
                  <div class="w-full h-80 relative flex items-center justify-center">
                    <img class="max-h-80 border border-gray-400" src={drawingSrc} alt="Type Drawing" />
                  </div>
                {:else}
                  <div class="w-full h-80 relative">
                    <ViewportLg padding={30} paddingBottom={70} drawing={typeDrawingDetailed(type)} />
                  </div>
                {/if}
              {/key}
            </div>
          </Link>
          {#if type.drawing && type.replace_with_drawing && !disabled}
            <button
              use:tooltip={{ text: "Remove Drawing" }}
              class="absolute top-0 right-0 text-gray-500 hover:text-black rounded p-1 hover:bg-gray-100"
              on:click|stopPropagation={removeDrawing}>
              <CircleXIcon />
            </button>
          {/if}
        </div>
      </SidebarSection>
    {:else}
      <SidebarSection>
        <div class="relative">
          <Link to={`types/${type.id}`}>
            <div class="flex items-center justify-center">
              {#key refreshKey}
                {#if type.drawing && type.replace_with_drawing}
                  <div class="w-36 h-36 relative flex items-center justify-center">
                    <img class="max-h-36 border border-gray-400" src={drawingSrc} alt="Type Drawing" />
                  </div>
                {:else}
                  <div class="w-36 h-36 relative">
                    <Viewport padding={1} drawing={typeDrawing(type)} width={136} height={136} />
                  </div>
                {/if}
              {/key}
            </div>
          </Link>
          {#if type.drawing && type.replace_with_drawing && !disabled}
            <button
              use:tooltip={{ text: "Remove Drawing" }}
              class="absolute top-0 right-0 text-gray-500 hover:text-black rounded p-1 hover:bg-gray-100"
              on:click|stopPropagation={removeDrawing}>
              <CircleXIcon />
            </button>
          {/if}
        </div>
      </SidebarSection>
    {/if}
  </div>
{/if}
<SidebarSection>
  <h3 class="px-2 mb-2">Properties</h3>
  <TextInput
    label="Mark"
    {disabled}
    labelWidth="7.5rem"
    value={type.mark}
    on:input={(evt) => updateType(type.id, "mark", evt.detail.value)} />
  {#if $makeupLibrary?.length}
    <LibraryInput
      label="Product"
      title="Product Library"
      {disabled}
      labelWidth="7.5rem"
      nullText="Generic Product"
      value={product(type)}
      preLibrary={genericLibrary()}
      library={$makeupLibrary}
      on:input={chooseProduct}>
      <div slot="prepend">Choose a generic template to begin:</div>
      <svelte:fragment slot="pend">
        {#if makeups.length || $suppliers?.length > 1}
          <div>Or select a supplier-defined product:</div>
        {/if}
        {#if $suppliers?.length > 1}
          <SelectInput
            border
            label="Supplier"
            value={$supplier?.id}
            options={$suppliers.map((s) => ({ value: s.id, label: s.name }))}
            on:input={updateSupplier} />
        {:else if $suppliers?.length === 1}
          <div class="font-bold mt-2">{$supplier?.name}</div>
        {/if}
      </svelte:fragment>
      <MakeupThumbnail slot="thumbnail" let:record {record} />
      <div slot="caption" let:record>{record?.name}</div>
    </LibraryInput>
  {/if}
  {#if !type.product_id}
    <TextInput
      label="Name"
      {disabled}
      labelWidth="7.5rem"
      value={type.name}
      on:input={(evt) => updateType(type.id, "name", evt.detail.value)} />
  {/if}
  {#if type.data !== "Mixed"}
    {#if !type.product_id}
      <DimtextInput
        label="Desired Thickness"
        {disabled}
        labelWidth="7.5rem"
        value={type.desired_thickness}
        {settings}
        allowNull
        highlightNull={false}
        on:input={(e) => updateType(type.id, "desired_thickness", e.detail.value)} />
    {/if}
    <TextInput
      label="Computed Thickness"
      readonly
      {disabled}
      labelWidth="7.5rem"
      value={totalThickness ?? ""} />
  {/if}
  <FlagInput
    label="Flag"
    {disabled}
    labelWidth="7.5rem"
    value={type.approval_status}
    on:input={(e) => updateType(type.id, "approval_status", e.detail.value)} />
  {#if type.drawing}
    <BooleanInput
      label="Replace with Drawing"
      {disabled}
      labelWidth="7.5rem"
      value={type.replace_with_drawing}
      on:input={(e) => updateType(type.id, "replace_with_drawing", e.detail.value)} />
  {/if}
  <BooleanInput
    label="Flip Orientation"
    {disabled}
    labelWidth="7.5rem"
    value={type.data?.flip_orientation}
    on:input={(e) => updateType(type.id, "data.flip_orientation", e.detail.value)} />
  <ContenteditableInput
    label="Notes"
    {disabled}
    labelWidth="7.5rem"
    value={type.notes}
    on:input={(evt) => updateType(type.id, "notes", evt.detail.value)} />
  <TextInput
    label="Scope"
    {disabled}
    labelWidth="7.5rem"
    value={type.scope}
    on:input={(evt) => updateType(type.id, "scope", evt.detail.value)} />
</SidebarSection>
